import React, { useEffect, useState } from 'react';
import { BadgeCheck, MailWarning } from 'lucide-react';
import { authService } from '../../services/api';

interface Props {
  verified: boolean;
  onVerified: () => void;
}

const EmailVerification: React.FC<Props> = ({ verified, onVerified }) => {
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 text-sm text-green-700">
        <BadgeCheck className="h-4 w-4" /> Email verified
      </span>
    );
  }

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await authService.sendEmailVerification();
      if (res.status === 'already_verified') return onVerified();
      setCodeSent(true);
      setCooldown(60);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send verification email');
      if (err.response?.data?.retryAfter) {
        setCodeSent(true);
        setCooldown(err.response.data.retryAfter);
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      await authService.verifyEmail(code.trim());
      onVerified();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
      <div className="flex items-center gap-2 text-amber-800">
        <MailWarning className="h-4 w-4" />
        <span>Your email address is not verified.</span>
      </div>
      {!codeSent ? (
        <button onClick={send} disabled={busy} className="mt-2 px-3 py-1.5 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
          {busy ? 'Sending…' : 'Send verification code'}
        </button>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="6-digit code"
            aria-label="Verification code"
            className="w-32 px-3 py-1.5 border border-gray-300 rounded-md tracking-widest"
          />
          <button onClick={verify} disabled={busy || code.length !== 6} className="px-3 py-1.5 text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
            Verify
          </button>
          <button onClick={send} disabled={busy || cooldown > 0} className="px-3 py-1.5 text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline">
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </button>
        </div>
      )}
      {codeSent && <p className="mt-1 text-xs text-gray-600">We emailed you a code. It expires in 15 minutes.</p>}
      {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
    </div>
  );
};

export default EmailVerification;
